import { redirect } from '@tanstack/react-router';

import { hasPermission } from '@nucleus-commerce/admin-kit/permissions';
import { ApiError } from '@nucleus-commerce/api-client/transport';

import type { Me } from '@/shared/session/session';

import type { RouterContext } from './router';

interface GuardInput {
  context: RouterContext;
  location: { href: string };
}

/** beforeLoad: resolves the session or redirects to login with a return URL. */
export async function requireSession({ context, location }: GuardInput): Promise<Me> {
  const me = await context.session.ensureSession(context.queryClient);
  if (!me) {
    throw redirect({ to: '/login', search: { redirect: location.href } });
  }
  return me;
}

/** beforeLoad: a suspended or closed organization can only sign out; the error boundary explains why. */
export function requireActiveOrganisation(me: Me): void {
  if (me.organization.status !== 'active') {
    throw new ApiError({
      kind: 'forbidden',
      code: 'ORGANIZATION_SUSPENDED',
      message: `Organization is ${me.organization.status}`,
    });
  }
}

/** Where a staff member who still has to enrol in two-factor may be. */
export const TWO_FACTOR_SETUP_PATH = '/account/security';

/**
 * beforeLoad: while the organisation requires two-factor and this account has none, the backend
 * answers 403 TWO_FACTOR_REQUIRED to everything else, so the only useful screen is the setup.
 */
export function requireTwoFactorEnrolment(me: Me, location: { pathname: string }): void {
  if (me.twoFactorRequired && location.pathname !== TWO_FACTOR_SETUP_PATH) {
    throw redirect({ to: TWO_FACTOR_SETUP_PATH });
  }
}

/** beforeLoad: throws a forbidden ApiError so the route error boundary renders "not permitted". */
export function requirePermission(
  me: Me,
  permission: string | string[],
  mode: 'all' | 'any' = 'all',
): void {
  if (!hasPermission(me.permissions, permission, mode)) {
    const list = Array.isArray(permission) ? permission : [permission];
    throw new ApiError({
      kind: 'forbidden',
      code: 'PERMISSION_DENIED',
      message: `Missing permission ${list.join(', ')}`,
    });
  }
}

/** For the portal landing and login: send an already signed-in user to the organisation home. */
export async function redirectIfAuthenticated({
  context,
}: {
  context: RouterContext;
}): Promise<void> {
  const me = await context.session.ensureSession(context.queryClient);
  if (me) throw redirect({ to: '/org' });
}
